import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../../services/api.ts";
import { getModelsOnly, getModel } from "../../services/aiModels";
import { toast } from "react-toastify";
import Navbar from "../navbar/Navbar";
import "./ViewerCreationLab.css";

const VIEWER_TYPES = [
    { value: "ohif", label: "OHIF Basic", icon: "fa-eye", color: "#007bff", category: "dicom" },
    { value: "ohif-segmentation", label: "OHIF Segmentation", icon: "fa-cut", color: "#dc3545", category: "dicom" },
    { value: "ohif-3d", label: "OHIF 3D", icon: "fa-cube", color: "#28a745", category: "dicom" },
    { value: "ohif-microscopy", label: "OHIF Microscopy", icon: "fa-microscope", color: "#6f42c1", category: "dicom" },
    { value: "kitware-volview", label: "Kitware VolView", icon: "fa-cube", color: "#fd7e14", category: "dicom" },
    { value: "stone-web-viewer", label: "Stone Viewer", icon: "fa-image", color: "#17a2b8", category: "dicom" },
    { value: "dv2", label: "DV2 Multimodal", icon: "fa-desktop", color: "#20c997", category: "multimodal" },
    { value: "discovery-viewer-test", label: "Discovery Test", icon: "fa-flask", color: "#e83e8c", category: "multimodal" },
    { value: "image-viewer", label: "Image Viewer", icon: "fa-image", color: "#6c757d", category: "images" },
    { value: "document-viewer", label: "Document Viewer", icon: "fa-file-text", color: "#795548", category: "documents" },
    { value: "ehr-viewer", label: "EHR Viewer", icon: "fa-heartbeat", color: "#dc3545", category: "ehr" },
    { value: "signal-viewer", label: "Signal Viewer", icon: "fa-wave-square", color: "#6610f2", category: "signals" },
    { value: "pme", label: "PME Viewer", icon: "fa-microscope", color: "#e91e63", category: "pathology" },
    { value: "trame-slicer", label: "3D Slicer", icon: "fa-cubes", color: "#0d6efd", category: "dicom" },
    { value: "cvat", label: "CVAT Video Annotation", icon: "fa-video-camera", color: "#0d47a1", category: "video" },
];

// Components available in scrollable layout mode. Runtime renderer (phase b) will
// switch on `type` and render the corresponding widget. `ai_panel` and `nav` are
// placeholders for now per spec.
const COMPONENT_TYPES = [
    { value: "text", label: "Text card", icon: "fa-file-text", defaults: { title: "Section title", body: "Section content..." } },
    { value: "viewer", label: "Viewer", icon: "fa-eye", defaults: { viewer_type: "ohif" } },
    { value: "slider", label: "Slider", icon: "fa-sliders", defaults: { label: "Confidence", min: 0, max: 100, value: 50 } },
    { value: "input", label: "Text input", icon: "fa-pencil", defaults: { label: "Input", placeholder: "Enter value..." } },
    { value: "textarea", label: "Textarea", icon: "fa-align-left", defaults: { label: "Notes", placeholder: "Enter notes...", rows: 3 } },
    { value: "submit", label: "Submit button", icon: "fa-check", defaults: { label: "Submit", reveals: [], reveal_mode: "all", reveal_weights: {}, submit_fields: [], required_fields: [], lock_after_submit: false } },
    { value: "ai_panel", label: "AI model panel", icon: "fa-robot", defaults: { title: "AI assist", body: "", ai_model_id: null, ai_model_name: "", parameters_schema: {}, param_modes: {}, param_values: {}, context_kinds: ["images", "documents", "imaging_metadata"], max_runs: 1 } },
    { value: "nav", label: "Prev / Next case", icon: "fa-arrows-h", defaults: { direction: "both" } },
    // Groups are containers: their `components` field (sibling of `props`) holds
    // nested children. Revealing a group reveals all its children with it.
    { value: "group", label: "Group / Section", icon: "fa-folder", defaults: { title: "Section title", description: "" } },
];

const makeId = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
const makeEmptyColumns = (n) => Array.from({ length: n }, (_, i) => ({ id: `col-${i + 1}`, components: [] }));

// ---- Tree helpers (groups can contain nested components) -------------------
// Top-level components live in a column's `components` array. Components of
// type 'group' carry their own `components` array. These helpers walk both
// levels uniformly so handlers don't need to know whether the target is a
// top-level component or a group child.

const flattenComponents = (components) => {
    const out = [];
    for (const c of components || []) {
        out.push(c);
        if (c.type === "group" && Array.isArray(c.components)) {
            out.push(...flattenComponents(c.components));
        }
    }
    return out;
};

const addToTree = (components, parentId, newComp) =>
    (components || []).map((c) => {
        if (c.id === parentId && c.type === "group") {
            return { ...c, components: [...(c.components || []), newComp] };
        }
        if (c.type === "group" && Array.isArray(c.components)) {
            return { ...c, components: addToTree(c.components, parentId, newComp) };
        }
        return c;
    });

const removeFromTree = (components, compId) =>
    (components || [])
        .filter((c) => c.id !== compId)
        .map((c) => {
            if (c.type === "group" && Array.isArray(c.components)) {
                return { ...c, components: removeFromTree(c.components, compId) };
            }
            return c;
        });

const updateInTree = (components, compId, patch) =>
    (components || []).map((c) => {
        if (c.id === compId) return { ...c, props: { ...c.props, ...patch } };
        if (c.type === "group" && Array.isArray(c.components)) {
            return { ...c, components: updateInTree(c.components, compId, patch) };
        }
        return c;
    });

const moveInTree = (components, compId, direction) => {
    const idx = (components || []).findIndex((c) => c.id === compId);
    if (idx >= 0) {
        const newIdx = direction === "up" ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= components.length) return components;
        const out = [...components];
        [out[idx], out[newIdx]] = [out[newIdx], out[idx]];
        return out;
    }
    return (components || []).map((c) => {
        if (c.type === "group" && Array.isArray(c.components)) {
            return { ...c, components: moveInTree(c.components, compId, direction) };
        }
        return c;
    });
};

// When a component is deleted, prune it from any submit's reveals + reveal_weights.
const cleanRevealsFromTree = (components, deletedId) =>
    (components || []).map((c) => {
        let next = c;
        if (c.type === "submit") {
            const reveals = Array.isArray(c.props?.reveals)
                ? c.props.reveals.filter((rid) => rid !== deletedId)
                : c.props?.reveals;
            const weights = c.props?.reveal_weights && typeof c.props.reveal_weights === "object"
                ? Object.fromEntries(Object.entries(c.props.reveal_weights).filter(([k]) => k !== deletedId))
                : c.props?.reveal_weights;
            if (reveals !== c.props?.reveals || weights !== c.props?.reveal_weights) {
                next = { ...c, props: { ...c.props, reveals, reveal_weights: weights } };
            }
        }
        if (next.type === "group" && Array.isArray(next.components)) {
            return { ...next, components: cleanRevealsFromTree(next.components, deletedId) };
        }
        return next;
    });

const LAYOUT_PRESETS = [
    { id: "single", name: "Single View", icon: "fa-square", splits: [{ id: 1, x: 0, y: 0, w: 100, h: 100 }] },
    { id: "horizontal-2", name: "Horizontal Split", icon: "fa-columns", splits: [
        { id: 1, x: 0, y: 0, w: 50, h: 100 },
        { id: 2, x: 50, y: 0, w: 50, h: 100 }
    ]},
    { id: "vertical-2", name: "Vertical Split", icon: "fa-bars", splits: [
        { id: 1, x: 0, y: 0, w: 100, h: 50 },
        { id: 2, x: 0, y: 50, w: 100, h: 50 }
    ]},
    { id: "grid-4", name: "Quad View", icon: "fa-th-large", splits: [
        { id: 1, x: 0, y: 0, w: 50, h: 50 },
        { id: 2, x: 50, y: 0, w: 50, h: 50 },
        { id: 3, x: 0, y: 50, w: 50, h: 50 },
        { id: 4, x: 50, y: 50, w: 50, h: 50 }
    ]},
    { id: "main-side", name: "Main + Side", icon: "fa-columns", splits: [
        { id: 1, x: 0, y: 0, w: 70, h: 100 },
        { id: 2, x: 70, y: 0, w: 30, h: 100 }
    ]},
    { id: "main-sidebar", name: "Main + Sidebar", icon: "fa-columns", splits: [
        { id: 1, x: 0, y: 0, w: 70, h: 100 },
        { id: 2, x: 70, y: 0, w: 30, h: 50 },
        { id: 3, x: 70, y: 50, w: 30, h: 50 }
    ]},
    { id: "triple-horizontal", name: "Triple Horizontal", icon: "fa-ellipsis-h", splits: [
        { id: 1, x: 0, y: 0, w: 33.33, h: 100 },
        { id: 2, x: 33.33, y: 0, w: 33.33, h: 100 },
        { id: 3, x: 66.66, y: 0, w: 33.34, h: 100 }
    ]},
    { id: "main-half-split", name: "Main + Half Split", icon: "fa-window-restore", splits: [
        { id: 1, x: 0, y: 0, w: 50, h: 100 },
        { id: 2, x: 50, y: 0, w: 50, h: 50 },
        { id: 3, x: 50, y: 50, w: 50, h: 50 }
    ]},
    { id: "top-split-bottom", name: "Top Split + Bottom", icon: "fa-window-maximize", splits: [
        { id: 1, x: 0, y: 0, w: 50, h: 66.66 },
        { id: 2, x: 50, y: 0, w: 50, h: 66.66 },
        { id: 3, x: 0, y: 66.66, w: 100, h: 33.34 }
    ]},
];

// Collapsible accordion section for the right sidebar. Matches the DVfrontend
// visual pattern — chevron rotates on open, body slides down.
const AccordionSection = ({ title, icon, children, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="lab-accordion">
            <button
                type="button"
                className={`lab-accordion-header ${open ? "open" : ""}`}
                onClick={() => setOpen((o) => !o)}
            >
                <i className={`fa ${icon} accordion-icon`}></i>
                <span>{title}</span>
                <i className="fa fa-chevron-down accordion-chevron"></i>
            </button>
            {open && <div className="lab-accordion-body">{children}</div>}
        </div>
    );
};

const ViewerCreationLab = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editingViewerId = searchParams.get("viewer_id");
    const isEditing = !!editingViewerId;

    const [viewerName, setViewerName] = useState("");
    const [viewerDescription, setViewerDescription] = useState("");
    const [selectedLayout, setSelectedLayout] = useState(LAYOUT_PRESETS[0]);
    const [panelAssignments, setPanelAssignments] = useState({});
    const [selectedPanel, setSelectedPanel] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEditing);
    const [robotMessage, setRobotMessage] = useState(isEditing
        ? "Loading existing viewer configuration..."
        : "Welcome to the Viewer Creation Lab, Commander! Select a layout template to begin designing your exploration tool.");
    const [robotAnimating, setRobotAnimating] = useState(false);

    // Scrollable layout mode — alternative to the fixed-grid presets above. A
    // schema-driven, column-stacked layout the runtime (phase b) will render
    // top-to-bottom with scrolling, expandable viewers, and submit-driven reveal.
    const [layoutMode, setLayoutMode] = useState("fixed"); // 'fixed' | 'scrollable'
    const [columnCount, setColumnCount] = useState(2);
    const [scrollableColumns, setScrollableColumns] = useState(() => makeEmptyColumns(2));
    const [selectedComponentId, setSelectedComponentId] = useState(null);
    const [addMenuColumnId, setAddMenuColumnId] = useState(null);

    // Hydrate state from an existing viewer when ?viewer_id=N is in the URL.
    useEffect(() => {
        if (!editingViewerId) return;
        let cancelled = false;
        (async () => {
            try {
                const resp = await API.get(`/viewers/${editingViewerId}/`);
                if (cancelled) return;
                const v = resp.data || {};
                setViewerName(v.name || "");
                setViewerDescription(v.description || "");
                const cfg = v.config || {};
                if (cfg.layout_mode === "scrollable" && cfg.scrollable_layout?.columns) {
                    setLayoutMode("scrollable");
                    const cols = cfg.scrollable_layout.columns;
                    setColumnCount(cols.length || 1);
                    setScrollableColumns(cols);
                } else {
                    setLayoutMode("fixed");
                    const preset = LAYOUT_PRESETS.find((p) => p.id === cfg.layout) || LAYOUT_PRESETS[0];
                    setSelectedLayout(preset);
                    const assignments = {};
                    (cfg.panels || []).forEach((p) => {
                        if (p.id != null && p.viewer_type) assignments[p.id] = p.viewer_type;
                    });
                    setPanelAssignments(assignments);
                }
            } catch (err) {
                console.error("Error loading viewer for edit:", err);
                toast.error("Failed to load viewer for editing.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [editingViewerId]);

    const allScrollableComponents = scrollableColumns.flatMap((col) => flattenComponents(col.components));
    const selectedComponent = allScrollableComponents.find((c) => c.id === selectedComponentId) || null;

    // User-accessible AI models, loaded once and reused by every AI panel
    // inspector. Empty array while loading or if the user has no models.
    const [aiModels, setAiModels] = useState([]);
    useEffect(() => {
        let cancelled = false;
        getModelsOnly()
            .then((data) => { if (!cancelled) setAiModels(Array.isArray(data) ? data : []); })
            .catch((err) => { console.error("Failed to load AI models for Lab:", err); });
        return () => { cancelled = true; };
    }, []);

    const handleColumnCountChange = (n) => {
        setColumnCount(n);
        setScrollableColumns((prev) => {
            if (n === prev.length) return prev;
            if (n > prev.length) {
                const extra = Array.from({ length: n - prev.length }, (_, i) => ({
                    id: `col-${prev.length + i + 1}`,
                    components: [],
                }));
                return [...prev, ...extra];
            }
            // Shrinking: keep first n. Components in dropped columns are lost — warn user.
            const dropped = prev.slice(n).reduce((sum, c) => sum + flattenComponents(c.components).length, 0);
            if (dropped > 0) toast.warning(`${dropped} component(s) removed with the dropped column(s).`);
            return prev.slice(0, n);
        });
        setSelectedComponentId(null);
        setAddMenuColumnId(null);
    };

    // parentId is either a column id (top-level add) or a group id (nested add).
    const handleAddComponent = (parentId, componentType) => {
        const def = COMPONENT_TYPES.find((c) => c.value === componentType);
        if (!def) return;
        const newComp = {
            id: makeId("comp"),
            type: componentType,
            // Deep-clone defaults so each instance has its own props (arrays especially).
            props: JSON.parse(JSON.stringify(def.defaults)),
        };
        // Groups carry a `components` array as a sibling of `props`.
        if (componentType === "group") newComp.components = [];

        setScrollableColumns((prev) =>
            prev.map((col) => {
                if (col.id === parentId) {
                    return { ...col, components: [...col.components, newComp] };
                }
                return { ...col, components: addToTree(col.components, parentId, newComp) };
            })
        );
        setSelectedComponentId(newComp.id);
        setAddMenuColumnId(null);
    };

    const handleRemoveComponent = (compId) => {
        setScrollableColumns((prev) =>
            prev.map((col) => {
                let comps = removeFromTree(col.components, compId);
                comps = cleanRevealsFromTree(comps, compId);
                return { ...col, components: comps };
            })
        );
        if (selectedComponentId === compId) setSelectedComponentId(null);
    };

    const handleMoveComponent = (compId, direction) => {
        setScrollableColumns((prev) =>
            prev.map((col) => ({
                ...col,
                components: moveInTree(col.components, compId, direction),
            }))
        );
    };

    const handleUpdateComponentProps = (compId, patch) => {
        setScrollableColumns((prev) =>
            prev.map((col) => ({
                ...col,
                components: updateInTree(col.components, compId, patch),
            }))
        );
    };

    // Robot messages for different actions
    const robotMessages = {
        selectLayout: "Excellent choice! Now assign viewers to each panel by clicking on them.",
        assignViewer: "Viewer module installed successfully! Continue configuring or save your creation.",
        panelSelect: "Panel selected. Choose a viewer module from the dock to assign it.",
        saveStart: "Initiating save sequence... Stand by, Commander.",
        saveSuccess: "Mission accomplished! Your viewer has been deployed to the fleet.",
        saveError: "Warning! Save sequence failed. Please check your configuration.",
        nameRequired: "Alert! Viewer designation required. Please enter a name.",
    };

    const updateRobotMessage = useCallback((messageKey) => {
        setRobotAnimating(true);
        setRobotMessage(robotMessages[messageKey] || messageKey);
        setTimeout(() => setRobotAnimating(false), 500);
    }, []);

    const handleLayoutSelect = (layout) => {
        setSelectedLayout(layout);
        setPanelAssignments({});
        setSelectedPanel(null);
        updateRobotMessage("selectLayout");
    };

    const handlePanelClick = (panelId) => {
        setSelectedPanel(panelId);
        updateRobotMessage("panelSelect");
    };

    const handleViewerAssign = (viewerType) => {
        if (selectedPanel) {
            setPanelAssignments(prev => ({
                ...prev,
                [selectedPanel]: viewerType
            }));
            updateRobotMessage("assignViewer");
            // Auto-select next empty panel
            const nextEmpty = selectedLayout.splits.find(s => !panelAssignments[s.id] && s.id !== selectedPanel);
            setSelectedPanel(nextEmpty ? nextEmpty.id : null);
        }
    };

    const handleSave = async () => {
        if (!viewerName.trim()) {
            updateRobotMessage("nameRequired");
            toast.warning("Please enter a viewer name");
            return;
        }

        updateRobotMessage("saveStart");
        setSaving(true);

        try {
            let config;
            let viewerType;
            let icon;
            let color;

            if (layoutMode === "scrollable") {
                config = {
                    layout_mode: "scrollable",
                    scrollable_layout: { columns: scrollableColumns },
                };
                // Scrollable layouts are always custom (multi-component case pages).
                viewerType = "custom";
                icon = "fa-th-list";
                color = "#0dcaf0";
            } else {
                config = {
                    layout_mode: "fixed",
                    layout: selectedLayout.id,
                    panels: selectedLayout.splits.map(split => ({
                        ...split,
                        viewer_type: panelAssignments[split.id] || null
                    }))
                };
                const primaryViewerType = panelAssignments[1] || "ohif";
                viewerType = selectedLayout.splits.length > 1 ? "custom" : primaryViewerType;
                icon = selectedLayout.splits.length > 1
                    ? "fa-th-large"
                    : VIEWER_TYPES.find(v => v.value === primaryViewerType)?.icon || "fa-desktop";
                color = VIEWER_TYPES.find(v => v.value === primaryViewerType)?.color || "#007bff";
            }

            const payload = {
                name: viewerName,
                description: viewerDescription,
                viewer_type: viewerType,
                supported_data_types: "multimodal",
                icon,
                color,
                is_active: true,
                config,
            };

            if (isEditing) {
                await API.patch(`/viewers/${editingViewerId}/`, payload);
            } else {
                await API.post("/viewers/", payload);
            }

            updateRobotMessage("saveSuccess");
            toast.success(isEditing ? "Viewer updated successfully!" : "Viewer created successfully!");
            setTimeout(() => navigate("/Viewers"), 1500);
        } catch (err) {
            console.error("Error saving viewer:", err);
            updateRobotMessage("saveError");
            toast.error(isEditing ? "Failed to update viewer." : "Failed to create viewer.");
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (layoutMode === "scrollable") {
            setScrollableColumns(makeEmptyColumns(columnCount));
            setSelectedComponentId(null);
            setAddMenuColumnId(null);
        } else {
            setPanelAssignments({});
            setSelectedPanel(null);
        }
    };

    return (
        <div className="viewer-lab">
            {/* Space Background */}
            <div className="space-background">
                <div className="stars"></div>
                <div className="stars2"></div>
                <div className="stars3"></div>
            </div>

            <Navbar />

            {/* Top Bar */}
            <div className="lab-topbar">
                <div className="lab-topbar-center">
                    <span className="topbar-status-dot"></span>
                    <span className="lab-topbar-title">DISCOVERY VIEWER PREVIEW</span>
                </div>
                <div className="lab-topbar-actions">
                    <button className="lab-btn lab-btn-secondary" onClick={handleReset}>
                        <i className="fa fa-refresh me-2"></i>
                        {layoutMode === "scrollable" ? "Reset Components" : "Reset Panels"}
                    </button>
                    <button className="lab-btn lab-btn-primary" onClick={handleSave} disabled={saving || loading}>
                        {saving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                {isEditing ? "Updating..." : "Deploying..."}
                            </>
                        ) : (
                            <>
                                <i className="fa fa-rocket me-2"></i>
                                {isEditing ? "Update Viewer" : "Deploy Viewer"}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Main: preview + sidebar */}
            <div className="lab-main">
                {/* Preview Area (left) */}
                <div className="lab-preview-area">
                    {layoutMode === "fixed" ? (
                        <div className="screen-content">
                            {selectedLayout.splits.map(split => {
                                const assignedViewer = VIEWER_TYPES.find(v => v.value === panelAssignments[split.id]);
                                return (
                                    <div
                                        key={split.id}
                                        className={`screen-panel ${selectedPanel === split.id ? "selected" : ""} ${assignedViewer ? "assigned" : ""}`}
                                        style={{
                                            left: `${split.x}%`,
                                            top: `${split.y}%`,
                                            width: `${split.w}%`,
                                            height: `${split.h}%`,
                                            "--panel-color": assignedViewer?.color || "#3a3f47"
                                        }}
                                        onClick={() => handlePanelClick(split.id)}
                                    >
                                        {assignedViewer ? (
                                            <div className="panel-content">
                                                <i className={`fa ${assignedViewer.icon}`}></i>
                                                <span>{assignedViewer.label}</span>
                                            </div>
                                        ) : (
                                            <div className="panel-empty">
                                                <i className="fa fa-plus-circle"></i>
                                                <span>Panel {split.id}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="scrollable-preview-wrapper">
                            <ScrollablePreview
                                columns={scrollableColumns}
                                selectedComponentId={selectedComponentId}
                                onSelectComponent={setSelectedComponentId}
                                onRemoveComponent={handleRemoveComponent}
                                onMoveComponent={handleMoveComponent}
                                addMenuColumnId={addMenuColumnId}
                                onOpenAddMenu={setAddMenuColumnId}
                                onAddComponent={handleAddComponent}
                                viewerTypes={VIEWER_TYPES}
                            />
                        </div>
                    )}
                </div>

                {/* Config Sidebar (right) */}
                <div className="lab-sidebar">
                    {/* Robot avatar */}
                    <div className="sidebar-robot">
                        <div className={`robot-avatar robot-small ${robotAnimating ? "animating" : ""}`}>
                            <div className="robot-head">
                                <div className="robot-eye left"></div>
                                <div className="robot-eye right"></div>
                                <div className="robot-antenna"></div>
                            </div>
                        </div>
                        <div className="sidebar-robot-msg">{robotMessage}</div>
                    </div>

                    <AccordionSection title="VIEWER DESIGNATION" icon="fa-tag" defaultOpen>
                        <input
                            type="text"
                            className="lab-input"
                            placeholder="Enter viewer name..."
                            value={viewerName}
                            onChange={(e) => setViewerName(e.target.value)}
                        />
                        <textarea
                            className="lab-input lab-textarea"
                            placeholder="Mission description (optional)..."
                            value={viewerDescription}
                            onChange={(e) => setViewerDescription(e.target.value)}
                            rows={2}
                        />
                    </AccordionSection>

                    <AccordionSection title="LAYOUT MODE" icon="fa-cog">
                        <div className="lab-mode-toggle">
                            <button
                                type="button"
                                className={`lab-mode-btn ${layoutMode === "fixed" ? "active" : ""}`}
                                onClick={() => setLayoutMode("fixed")}
                            >
                                <i className="fa fa-th-large me-2"></i>Fixed grid
                            </button>
                            <button
                                type="button"
                                className={`lab-mode-btn ${layoutMode === "scrollable" ? "active" : ""}`}
                                onClick={() => setLayoutMode("scrollable")}
                            >
                                <i className="fa fa-th-list me-2"></i>Scrollable
                            </button>
                        </div>
                    </AccordionSection>

                    {layoutMode === "fixed" ? (
                        <>
                            <AccordionSection title="LAYOUT TEMPLATE" icon="fa-th-large">
                                <div className="layout-presets">
                                    {LAYOUT_PRESETS.map(layout => (
                                        <button
                                            key={layout.id}
                                            className={`layout-preset-btn ${selectedLayout.id === layout.id ? "active" : ""}`}
                                            onClick={() => handleLayoutSelect(layout)}
                                            title={layout.name}
                                        >
                                            <i className={`fa ${layout.icon}`}></i>
                                            <span>{layout.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </AccordionSection>

                            <AccordionSection title="VIEWER MODULES" icon="fa-plug">
                                <p className="section-hint">Click a panel, then select a module</p>
                                <div className="viewer-modules">
                                    {VIEWER_TYPES.map(viewer => (
                                        <button
                                            key={viewer.value}
                                            className="viewer-module-btn"
                                            onClick={() => handleViewerAssign(viewer.value)}
                                            disabled={!selectedPanel}
                                            style={{ "--module-color": viewer.color }}
                                        >
                                            <i className={`fa ${viewer.icon}`}></i>
                                            <span>{viewer.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </AccordionSection>
                        </>
                    ) : (
                        <>
                            <AccordionSection title="COLUMNS" icon="fa-columns">
                                <div className="column-count-buttons">
                                    {[1, 2, 3].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            className={`lab-mode-btn ${columnCount === n ? "active" : ""}`}
                                            onClick={() => handleColumnCountChange(n)}
                                        >
                                            {n} {n === 1 ? "column" : "columns"}
                                        </button>
                                    ))}
                                </div>
                            </AccordionSection>

                            {selectedComponent && (
                                <ComponentInspector
                                    component={selectedComponent}
                                    allComponents={allScrollableComponents}
                                    onChange={(patch) => handleUpdateComponentProps(selectedComponent.id, patch)}
                                    viewerTypes={VIEWER_TYPES}
                                    aiModels={aiModels}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// Scrollable layout — preview + per-component inspector
// ============================================================================

const ScrollablePreview = ({
    columns,
    selectedComponentId,
    onSelectComponent,
    onRemoveComponent,
    onMoveComponent,
    addMenuColumnId,
    onOpenAddMenu,
    onAddComponent,
    viewerTypes,
}) => {
    return (
        <div className={`scrollable-preview cols-${columns.length}`}>
            {columns.map((col) => (
                <div key={col.id} className="preview-column">
                    {col.components.map((comp, idx) => (
                        <PreviewComponentCard
                            key={comp.id}
                            comp={comp}
                            isFirst={idx === 0}
                            isLast={idx === col.components.length - 1}
                            viewerTypes={viewerTypes}
                            selectedComponentId={selectedComponentId}
                            onSelectComponent={onSelectComponent}
                            onRemoveComponent={onRemoveComponent}
                            onMoveComponent={onMoveComponent}
                            addMenuParentId={addMenuColumnId}
                            onOpenAddMenu={onOpenAddMenu}
                            onAddComponent={onAddComponent}
                        />
                    ))}

                    <AddComponentControl
                        parentId={col.id}
                        isOpen={addMenuColumnId === col.id}
                        onOpen={onOpenAddMenu}
                        onAdd={onAddComponent}
                    />
                </div>
            ))}
        </div>
    );
};

// Renders either the "+ Add component" button or the open palette of types.
const AddComponentControl = ({ parentId, isOpen, onOpen, onAdd }) => {
    if (!isOpen) {
        return (
            <button
                type="button"
                className="add-component-btn"
                onClick={(e) => { e.stopPropagation(); onOpen(parentId); }}
            >
                <i className="fa fa-plus me-2"></i>Add component
            </button>
        );
    }
    return (
        <div className="add-component-menu" onClick={(e) => e.stopPropagation()}>
            <div className="add-component-menu-header">
                <span>Add component</span>
                <button type="button" className="icon-btn" onClick={() => onOpen(null)} title="Close">
                    <i className="fa fa-times"></i>
                </button>
            </div>
            <div className="add-component-options">
                {COMPONENT_TYPES.map((ct) => (
                    <button
                        key={ct.value}
                        type="button"
                        className="add-component-option"
                        onClick={() => onAdd(parentId, ct.value)}
                    >
                        <i className={`fa ${ct.icon} me-2`}></i>
                        {ct.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

const PreviewComponentCard = ({
    comp, isFirst, isLast, viewerTypes,
    selectedComponentId, onSelectComponent, onRemoveComponent, onMoveComponent,
    addMenuParentId, onOpenAddMenu, onAddComponent,
}) => {
    const def = COMPONENT_TYPES.find((c) => c.value === comp.type);
    const label = def?.label || comp.type;
    const icon = def?.icon || "fa-square";
    const isSelected = selectedComponentId === comp.id;
    const isGroup = comp.type === "group";

    return (
        <div
            className={`preview-component ${isSelected ? "selected" : ""} preview-component-${comp.type}`}
            onClick={(e) => { e.stopPropagation(); onSelectComponent(comp.id); }}
        >
            <div className="preview-component-header">
                <span className="preview-component-title">
                    <i className={`fa ${icon} me-2`}></i>
                    {isGroup ? (comp.props?.title || label) : label}
                </span>
                <div className="preview-component-actions" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="icon-btn" onClick={() => onMoveComponent(comp.id, "up")} disabled={isFirst} title="Move up">
                        <i className="fa fa-arrow-up"></i>
                    </button>
                    <button type="button" className="icon-btn" onClick={() => onMoveComponent(comp.id, "down")} disabled={isLast} title="Move down">
                        <i className="fa fa-arrow-down"></i>
                    </button>
                    <button type="button" className="icon-btn danger" onClick={() => onRemoveComponent(comp.id)} title="Remove">
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            </div>

            {isGroup ? (
                <>
                    {comp.props?.description && (
                        <div className="preview-group-description">{comp.props.description}</div>
                    )}
                    <div className="preview-group-body">
                        {(comp.components || []).map((child, cidx) => (
                            <PreviewComponentCard
                                key={child.id}
                                comp={child}
                                isFirst={cidx === 0}
                                isLast={cidx === (comp.components.length - 1)}
                                viewerTypes={viewerTypes}
                                selectedComponentId={selectedComponentId}
                                onSelectComponent={onSelectComponent}
                                onRemoveComponent={onRemoveComponent}
                                onMoveComponent={onMoveComponent}
                                addMenuParentId={addMenuParentId}
                                onOpenAddMenu={onOpenAddMenu}
                                onAddComponent={onAddComponent}
                            />
                        ))}
                        <AddComponentControl
                            parentId={comp.id}
                            isOpen={addMenuParentId === comp.id}
                            onOpen={onOpenAddMenu}
                            onAdd={onAddComponent}
                        />
                    </div>
                </>
            ) : (
                <PreviewComponentBody comp={comp} viewerTypes={viewerTypes} />
            )}
        </div>
    );
};

// Visual stand-ins so the lab preview communicates roughly what each component
// will look like at runtime. Real interactivity (slider drag, submit reveal) is
// the runtime renderer's job (phase b).
const PreviewComponentBody = ({ comp, viewerTypes }) => {
    const { type, props = {} } = comp;
    if (type === "text") {
        return (
            <div className="preview-component-body">
                {props.title && <div className="preview-text-title">{props.title}</div>}
                {props.body && <div className="preview-text-body">{props.body}</div>}
            </div>
        );
    }
    if (type === "ai_panel") {
        const paramCount = props.parameters_schema && typeof props.parameters_schema === "object"
            ? Object.keys(props.parameters_schema).length : 0;
        return (
            <div className="preview-component-body">
                {props.title && <div className="preview-text-title">{props.title}</div>}
                <div className="preview-text-body">
                    <i className="fa fa-robot me-2"></i>
                    {props.ai_model_name
                        ? `AI model assigned: ${props.ai_model_name}`
                        : "No model assigned — pick one in the inspector."}
                </div>
                {paramCount > 0 && (
                    <div className="text-muted small mt-1">{paramCount} parameter{paramCount !== 1 ? "s" : ""} configured</div>
                )}
            </div>
        );
    }
    if (type === "viewer") {
        const v = viewerTypes.find((vt) => vt.value === props.viewer_type);
        return (
            <div className="preview-viewer-block">
                <div className="preview-viewer-toolbar">
                    <i className={`fa ${v?.icon || "fa-eye"} me-2`}></i>
                    {v?.label || props.viewer_type || "Viewer"}
                    <i className="fa fa-expand expand-icon" title="Expand button (runtime)"></i>
                </div>
                <div className="preview-viewer-stage">Viewer will render here</div>
            </div>
        );
    }
    if (type === "slider") {
        return (
            <div className="preview-component-body">
                <div className="preview-text-title">{props.label || "Slider"}</div>
                <input
                    type="range"
                    min={props.min ?? 0}
                    max={props.max ?? 100}
                    value={props.value ?? 50}
                    readOnly
                    className="form-range"
                />
            </div>
        );
    }
    if (type === "input") {
        return (
            <div className="preview-component-body">
                {props.label && <div className="preview-text-title">{props.label}</div>}
                <input type="text" className="form-control" placeholder={props.placeholder || ""} readOnly />
            </div>
        );
    }
    if (type === "textarea") {
        return (
            <div className="preview-component-body">
                {props.label && <div className="preview-text-title">{props.label}</div>}
                <textarea className="form-control" placeholder={props.placeholder || ""} rows={props.rows || 3} readOnly />
            </div>
        );
    }
    if (type === "submit") {
        const revealCount = Array.isArray(props.reveals) ? props.reveals.length : 0;
        return (
            <div className="preview-component-body">
                <button type="button" className="btn btn-info w-100" disabled>
                    {props.label || "Submit"}
                </button>
                {revealCount > 0 && (
                    <div className="preview-reveal-hint">
                        Reveals {revealCount} component{revealCount !== 1 ? "s" : ""} on submit.
                    </div>
                )}
            </div>
        );
    }
    if (type === "nav") {
        const forwardOnly = props.direction === "forward";
        return (
            <div className="preview-component-body preview-nav-body">
                {!forwardOnly && (
                    <button type="button" className="btn btn-outline-light btn-sm" disabled>
                        <i className="fa fa-arrow-left me-1"></i>Previous case
                    </button>
                )}
                <button type="button" className="btn btn-outline-light btn-sm" disabled>
                    Next case<i className="fa fa-arrow-right ms-1"></i>
                </button>
            </div>
        );
    }
    return null;
};

// Inspector body for the AI panel component. Lets the researcher bind a model
// from their model library, then mark each parameter as:
//   - "fixed":    researcher sets the value, reviewer never sees the field
//   - "editable": researcher sets a default, reviewer can still change it
//   - "open":     reviewer chooses (no default applied here)
// The selected model's parameters_schema is cached in the component's props so
// the runtime doesn't need a second round-trip to render the form.
// The patient-context section in the runtime AI panel can supply three
// distinct kinds of payload. The lab lets the researcher restrict which ones
// the reviewer is allowed to attach (e.g. text-only models don't benefit
// from screenshots — turn `images` off so the UI isn't misleading).
const CONTEXT_KIND_OPTIONS = [
    { value: "images", label: "Imaging screenshots", help: "Reviewer captures/uploads viewer screenshots (sent to vision-capable models)." },
    { value: "documents", label: "Document text", help: "Reviewer attaches the rendered text of a visible document viewer." },
    { value: "imaging_metadata", label: "Imaging metadata", help: "Reviewer attaches a structured summary of the patient's imaging series." },
];
const DEFAULT_CONTEXT_KINDS = ["images", "documents", "imaging_metadata"];

const AIPanelInspectorBody = ({ props, onChange, aiModels }) => {
    const modelId = props.ai_model_id;
    const schema = (props.parameters_schema && typeof props.parameters_schema === "object") ? props.parameters_schema : {};
    const modes = (props.param_modes && typeof props.param_modes === "object") ? props.param_modes : {};
    const values = (props.param_values && typeof props.param_values === "object") ? props.param_values : {};
    // Treat a missing `context_kinds` as "all enabled" so panels created before
    // this option existed keep their current behavior in the runtime.
    const contextKinds = Array.isArray(props.context_kinds) ? props.context_kinds : DEFAULT_CONTEXT_KINDS;
    const toggleContextKind = (kind) => {
        const next = contextKinds.includes(kind)
            ? contextKinds.filter((k) => k !== kind)
            : [...contextKinds, kind];
        onChange({ context_kinds: next });
    };

    const handleSelectModel = async (e) => {
        const newId = e.target.value;
        if (!newId) {
            onChange({ ai_model_id: null, ai_model_name: "", parameters_schema: {}, param_modes: {}, param_values: {} });
            return;
        }
        const summary = aiModels.find((m) => String(m.id) === String(newId));
        try {
            const detail = await getModel(newId);
            const newSchema = detail?.parameters_schema || {};
            const initialModes = {};
            const initialValues = {};
            Object.entries(newSchema).forEach(([key, cfg]) => {
                initialModes[key] = "open";
                if (cfg && cfg.default !== undefined) initialValues[key] = cfg.default;
            });
            onChange({
                ai_model_id: detail?.id || newId,
                ai_model_name: detail?.name || summary?.name || "",
                parameters_schema: newSchema,
                param_modes: initialModes,
                param_values: initialValues,
            });
        } catch (err) {
            console.error("Failed to load model detail:", err);
            toast.error("Failed to load AI model details.");
        }
    };

    const setMode = (key, mode) => onChange({ param_modes: { ...modes, [key]: mode } });
    const setValue = (key, value) => onChange({ param_values: { ...values, [key]: value } });

    const renderValueInput = (key, cfg) => {
        const v = values[key];
        const onChangeVal = (next) => setValue(key, next);
        switch (cfg.type) {
            case "boolean":
                return (
                    <label className="reveal-checkbox">
                        <input type="checkbox" checked={!!v} onChange={(e) => onChangeVal(e.target.checked)} />
                        <span>{cfg.label || key}</span>
                    </label>
                );
            case "select":
                return (
                    <select className="lab-input" value={v ?? ""} onChange={(e) => onChangeVal(e.target.value)}>
                        <option value="">— pick a default —</option>
                        {(cfg.options || []).map((opt) => {
                            const ov = typeof opt === "object" ? opt.value : opt;
                            const ol = typeof opt === "object" ? opt.label : opt;
                            return <option key={ov} value={ov}>{ol}</option>;
                        })}
                    </select>
                );
            case "multiselect":
                return (
                    <div className="text-muted small">
                        Multi-select defaults aren't editable here yet; mark as "Reviewer chooses".
                    </div>
                );
            case "number":
                return (
                    <input
                        type="number"
                        className="lab-input"
                        value={v ?? ""}
                        onChange={(e) => onChangeVal(e.target.value === "" ? "" : Number(e.target.value))}
                    />
                );
            case "textarea":
                return (
                    <textarea
                        className="lab-input lab-textarea"
                        rows={3}
                        value={v ?? ""}
                        onChange={(e) => onChangeVal(e.target.value)}
                    />
                );
            default:
                return (
                    <input
                        type="text"
                        className="lab-input"
                        value={v ?? ""}
                        onChange={(e) => onChangeVal(e.target.value)}
                    />
                );
        }
    };

    return (
        <>
            <input className="lab-input" placeholder="Panel title" value={props.title || ""} onChange={(e) => onChange({ title: e.target.value })} />

            <div className="section-hint mt-2 mb-1">AI model</div>
            <select className="lab-input" value={modelId || ""} onChange={handleSelectModel}>
                <option value="">— pick a model —</option>
                {aiModels.length === 0 && <option value="" disabled>(no models available)</option>}
                {aiModels.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}{m.version ? ` (v${m.version})` : ""}</option>
                ))}
            </select>

            {modelId && Object.keys(schema).length === 0 && (
                <div className="text-muted small mt-2">This model exposes no parameters. The reviewer will see just a Run button.</div>
            )}

            {modelId && (
                <div className="inspector-reveals mt-3">
                    <div className="section-hint mb-1">Max model runs per case</div>
                    <input
                        type="number"
                        min="1"
                        max="100"
                        className="lab-input"
                        value={Number.isFinite(Number(props.max_runs)) ? Number(props.max_runs) : 1}
                        onChange={(e) => {
                            const n = Number(e.target.value);
                            const clamped = Number.isFinite(n) ? Math.max(1, Math.min(100, Math.round(n))) : 1;
                            onChange({ max_runs: clamped });
                        }}
                    />
                    <div className="text-muted small">
                        How many times the reviewer can run this model on the same case.
                        Each run keeps its own prompt + answer in the panel, so the reviewer
                        can ask follow-up questions.
                    </div>
                </div>
            )}

            {modelId && (
                <div className="inspector-reveals mt-3">
                    <div className="section-hint mb-1">Patient context</div>
                    <div className="text-muted small" style={{ marginBottom: 6 }}>
                        Which kinds of context the reviewer can attach when the model's
                        "Include patient context" option is on. Uncheck to hide the
                        corresponding section in the runtime AI panel.
                    </div>
                    {CONTEXT_KIND_OPTIONS.map((opt) => (
                        <label
                            key={opt.value}
                            className="reveal-checkbox"
                            style={{ display: "block", marginBottom: 6, alignItems: "flex-start" }}
                        >
                            <input
                                type="checkbox"
                                checked={contextKinds.includes(opt.value)}
                                onChange={() => toggleContextKind(opt.value)}
                                style={{ marginTop: 3 }}
                            />
                            <span>
                                <span style={{ fontWeight: 600, color: "#e6ecf6" }}>{opt.label}</span>
                                <div className="text-muted small">{opt.help}</div>
                            </span>
                        </label>
                    ))}
                </div>
            )}

            {modelId && Object.keys(schema).length > 0 && (
                <div className="inspector-reveals mt-2">
                    <div className="section-hint mb-1">Parameters</div>
                    {Object.entries(schema).map(([key, cfg]) => {
                        const mode = String(modes[key] || "open").toLowerCase();
                        const badge = mode === "fixed"
                            ? { text: "Hidden", color: "#dc3545", bg: "rgba(220, 53, 69, 0.15)" }
                            : mode === "editable"
                                ? { text: "Pre-filled", color: "#ffc107", bg: "rgba(255, 193, 7, 0.15)" }
                                : { text: "Reviewer", color: "#28a745", bg: "rgba(40, 167, 69, 0.15)" };
                        return (
                            <div key={key} className="ai-param-config" style={{ borderBottom: "1px solid rgba(120, 140, 200, 0.18)", paddingBottom: 8, marginBottom: 8 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                                    <div style={{ fontWeight: 600, color: "#e6ecf6", fontSize: 13 }}>{cfg?.label || key}</div>
                                    <span style={{ background: badge.bg, color: badge.color, fontSize: 11, padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>{badge.text}</span>
                                </div>
                                {cfg?.description && (
                                    <div className="text-muted small" style={{ marginBottom: 4 }}>{cfg.description}</div>
                                )}
                                <select className="lab-input" value={mode} onChange={(e) => setMode(key, e.target.value)} style={{ marginBottom: 6 }}>
                                    <option value="open">Reviewer chooses (visible, empty)</option>
                                    <option value="editable">Pre-filled (visible, reviewer can edit)</option>
                                    <option value="fixed">Fixed (hidden from reviewer, your value is used)</option>
                                </select>
                                {(mode === "editable" || mode === "fixed") && renderValueInput(key, cfg || {})}
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

const ComponentInspector = ({ component, allComponents, onChange, viewerTypes, aiModels = [] }) => {
    const { type, props = {} } = component;
    const def = COMPONENT_TYPES.find((c) => c.value === type);

    const setProp = (key) => (e) => {
        const val = e.target.type === "number" ? Number(e.target.value) : e.target.value;
        onChange({ [key]: val });
    };

    return (
        <div className="config-section component-inspector">
            <h3 className="section-title">
                <i className={`fa ${def?.icon || "fa-cog"} me-2`}></i>
                {def?.label || type}
            </h3>

            {type === "text" && (
                <>
                    <input className="lab-input" placeholder="Title" value={props.title || ""} onChange={setProp("title")} />
                    <textarea className="lab-input lab-textarea" placeholder="Body" rows={4} value={props.body || ""} onChange={setProp("body")} />
                </>
            )}

            {type === "ai_panel" && (
                <AIPanelInspectorBody props={props} onChange={onChange} aiModels={aiModels} setProp={setProp} />
            )}

            {type === "viewer" && (
                <select className="lab-input" value={props.viewer_type || "ohif"} onChange={setProp("viewer_type")}>
                    {viewerTypes.map((v) => (
                        <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                </select>
            )}

            {type === "slider" && (
                <>
                    <input className="lab-input" placeholder="Label" value={props.label || ""} onChange={setProp("label")} />
                    <div className="d-flex gap-2">
                        <input className="lab-input" type="number" placeholder="Min" value={props.min ?? 0} onChange={setProp("min")} />
                        <input className="lab-input" type="number" placeholder="Max" value={props.max ?? 100} onChange={setProp("max")} />
                    </div>
                </>
            )}

            {(type === "input" || type === "textarea") && (
                <>
                    <input className="lab-input" placeholder="Label" value={props.label || ""} onChange={setProp("label")} />
                    <input className="lab-input" placeholder="Placeholder" value={props.placeholder || ""} onChange={setProp("placeholder")} />
                    {type === "textarea" && (
                        <input className="lab-input" type="number" placeholder="Rows" value={props.rows ?? 3} onChange={setProp("rows")} />
                    )}
                </>
            )}

            {type === "submit" && (
                <>
                    <input className="lab-input" placeholder="Button label" value={props.label || ""} onChange={setProp("label")} />
                    <div className="inspector-reveals">
                        <div className="section-hint mb-1">Reveals on click:</div>
                        {allComponents.filter((c) => c.id !== component.id).length === 0 ? (
                            <div className="text-muted small">No other components to reveal yet.</div>
                        ) : (
                            allComponents
                                .filter((c) => c.id !== component.id)
                                .map((c) => {
                                    const isChecked = Array.isArray(props.reveals) && props.reveals.includes(c.id);
                                    const otherDef = COMPONENT_TYPES.find((ct) => ct.value === c.type);
                                    const weights = props.reveal_weights && typeof props.reveal_weights === "object" ? props.reveal_weights : {};
                                    return (
                                        <div key={c.id} className="reveal-row" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <label className="reveal-checkbox" style={{ flex: 1 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                        const current = Array.isArray(props.reveals) ? props.reveals : [];
                                                        const next = e.target.checked
                                                            ? [...current, c.id]
                                                            : current.filter((id) => id !== c.id);
                                                        onChange({ reveals: next });
                                                    }}
                                                />
                                                <i className={`fa ${otherDef?.icon || "fa-square"} me-1`}></i>
                                                {otherDef?.label || c.type}
                                                {c.props?.title || c.props?.label ? ` — ${c.props.title || c.props.label}` : ""}
                                            </label>
                                            {isChecked && props.reveal_mode === "weighted_one" && (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    className="lab-input"
                                                    style={{ width: 70 }}
                                                    placeholder="weight"
                                                    value={weights[c.id] ?? 1}
                                                    onChange={(e) => {
                                                        const v = e.target.value === "" ? "" : Number(e.target.value);
                                                        onChange({ reveal_weights: { ...weights, [c.id]: v } });
                                                    }}
                                                    title="Higher weight = more likely to be revealed"
                                                />
                                            )}
                                        </div>
                                    );
                                })
                        )}
                    </div>
                    <div className="section-hint mt-2 mb-1">When clicked:</div>
                    <select
                        className="lab-input"
                        value={props.reveal_mode || "all"}
                        onChange={setProp("reveal_mode")}
                    >
                        <option value="all">Reveal all selected</option>
                        <option value="random_one">Reveal one — random (equal chance)</option>
                        <option value="weighted_one">Reveal one — weighted random</option>
                    </select>
                    {props.reveal_mode === "weighted_one" && (
                        <div className="text-muted small mt-1">
                            Weight ≥ 0 per option. Probability of each = its weight ÷ total. Default 1 if blank.
                        </div>
                    )}

                    {/* Fields-to-submit picker — defaults to ALL form fields
                        (empty list). Tick specific fields to scope this submit
                        button's save payload to just those answers. */}
                    {(() => {
                        const FIELD_TYPES = new Set(["input", "textarea", "slider"]);
                        const fieldOpts = allComponents.filter((c) => c.id !== component.id && FIELD_TYPES.has(c.type));
                        const selected = Array.isArray(props.submit_fields) ? props.submit_fields : [];
                        if (fieldOpts.length === 0) return null;
                        return (
                            <>
                                <div className="section-hint mt-3 mb-1">Fields submitted on click</div>
                                <div className="text-muted small" style={{ marginBottom: 6 }}>
                                    Leave all unticked to submit every form field on the page. Tick specific
                                    fields to submit only their answers (other fields' values won't be saved
                                    with this submit).
                                </div>
                                <div className="inspector-reveals">
                                    {fieldOpts.map((c) => {
                                        const isChecked = selected.includes(c.id);
                                        const otherDef = COMPONENT_TYPES.find((ct) => ct.value === c.type);
                                        return (
                                            <label key={c.id} className="reveal-checkbox" style={{ display: "block", marginBottom: 4 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                        const next = e.target.checked
                                                            ? [...selected, c.id]
                                                            : selected.filter((id) => id !== c.id);
                                                        onChange({ submit_fields: next });
                                                    }}
                                                />
                                                <i className={`fa ${otherDef?.icon || "fa-square"} me-1`}></i>
                                                {otherDef?.label || c.type}
                                                {c.props?.title || c.props?.label ? ` — ${c.props.title || c.props.label}` : ""}
                                            </label>
                                        );
                                    })}
                                </div>
                                <label className="reveal-checkbox mt-2" style={{ display: "block" }}>
                                    <input
                                        type="checkbox"
                                        checked={!!props.lock_after_submit}
                                        onChange={(e) => onChange({ lock_after_submit: e.target.checked })}
                                    />
                                    <span>Lock these fields after submit</span>
                                </label>
                                <div className="text-muted small">
                                    Once the reviewer clicks this submit, the listed fields become read-only.
                                    Other fields stay editable. Lock survives across re-renders within the same
                                    case but resets when the patient changes.
                                </div>

                                {/* Required-fields picker — the reviewer must fill each ticked
                                    field before this submit will fire. Independent of
                                    submit_fields: a field can be required but not part of
                                    this button's save scope (e.g. a shared "Reviewer name"
                                    that a later submit persists). */}
                                {(() => {
                                    const requiredSelected = Array.isArray(props.required_fields) ? props.required_fields : [];
                                    return (
                                        <>
                                            <div className="section-hint mt-3 mb-1">Required fields to submit</div>
                                            <div className="text-muted small" style={{ marginBottom: 6 }}>
                                                Tick fields the reviewer must fill before this submit will fire.
                                                Empty required fields block the click and are highlighted in red.
                                                Sliders count as "unanswered" until the reviewer clicks or drags
                                                the thumb — a single click on the current position confirms the
                                                default value.
                                            </div>
                                            <div className="inspector-reveals">
                                                {fieldOpts.map((c) => {
                                                    const isChecked = requiredSelected.includes(c.id);
                                                    const otherDef = COMPONENT_TYPES.find((ct) => ct.value === c.type);
                                                    return (
                                                        <label key={c.id} className="reveal-checkbox" style={{ display: "block", marginBottom: 4 }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(e) => {
                                                                    const next = e.target.checked
                                                                        ? [...requiredSelected, c.id]
                                                                        : requiredSelected.filter((id) => id !== c.id);
                                                                    onChange({ required_fields: next });
                                                                }}
                                                            />
                                                            <i className={`fa ${otherDef?.icon || "fa-square"} me-1`}></i>
                                                            {otherDef?.label || c.type}
                                                            {c.props?.title || c.props?.label ? ` — ${c.props.title || c.props.label}` : ""}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    );
                                })()}
                            </>
                        );
                    })()}
                </>
            )}

            {type === "nav" && (
                <>
                    <div className="section-hint mb-1">Direction</div>
                    <select
                        className="lab-input"
                        value={props.direction || "both"}
                        onChange={setProp("direction")}
                    >
                        <option value="both">Both (Prev + Next)</option>
                        <option value="forward">Forward only (Next)</option>
                    </select>
                    <div className="text-muted small mt-2">
                        Wired to the reader queue at runtime. "Forward only" hides the Previous button — useful for one-shot reviews where backtracking should not be allowed.
                    </div>
                </>
            )}

            {type === "group" && (
                <>
                    <input className="lab-input" placeholder="Title" value={props.title || ""} onChange={setProp("title")} />
                    <textarea
                        className="lab-input lab-textarea"
                        placeholder="Short description (optional)"
                        rows={3}
                        value={props.description || ""}
                        onChange={setProp("description")}
                    />
                    <div className="text-muted small mt-2">
                        Add components inside this group from the "+ Add component" button at the bottom of the group in the preview. When this group is in a Submit's "Reveals" list, all its children appear together when that Submit is clicked.
                    </div>
                </>
            )}
        </div>
    );
};

export default ViewerCreationLab;
